import React, { useState } from 'react';
import { Download, Building2, Users, Calendar, Briefcase, CheckCircle, AlertCircle } from 'lucide-react';

const fmt = (n) => parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShort = (n) => parseFloat(n || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 });

export default function MultiplePrestacionesView({
  data,
  scope,
  scopeName,
  departments,
  employees,
}) {
  const [sortField, setSortField] = useState('nombre');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedEmpleados = [...data.empleados].sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case 'nombre':
        valA = `${a.empleado.nombre} ${a.empleado.apellido}`;
        valB = `${b.empleado.nombre} ${b.empleado.apellido}`;
        break;
      case 'antiguedad':
        valA = a.anosServicio * 12 + a.mesesServicio;
        valB = b.anosServicio * 12 + b.mesesServicio;
        break;
      case 'total':
        valA = a.totalPrestaciones;
        valB = b.totalPrestaciones;
        break;
      case 'salario':
        valA = a.salarioPromedio;
        valB = b.salarioPromedio;
        break;
      default:
        valA = `${a.empleado.nombre} ${a.empleado.apellido}`;
        valB = `${b.empleado.nombre} ${b.empleado.apellido}`;
    }
    if (typeof valA === 'string') {
      return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
    return sortDir === 'asc' ? valA - valB : valB - valA;
  });

  const handleExportCSV = () => {
    const headers = ['Empleado', 'Departamento', 'Cargo', 'Antigüedad', 'Salario Promedio', 'Vacaciones', 'Navidad', 'Preaviso', 'Cesantía', 'Total'];
    const rows = sortedEmpleados.map(p => [
      `${p.empleado.nombre} ${p.empleado.apellido}`,
      p.empleado.departamento || 'Sin dept',
      p.empleado.cargo,
      `${p.anosServicio}a ${p.mesesServicio}m ${p.diasServicio ?? 0}d`,
      p.salarioPromedio.toFixed(2),
      p.vacaciones.monto.toFixed(2),
      p.salarioNavidad.monto.toFixed(2),
      p.preaviso.monto.toFixed(2),
      p.cesantia.monto.toFixed(2),
      p.totalPrestaciones.toFixed(2)
    ]);
    rows.push(['TOTALES', '', '', '', '',
      data.totales.vacaciones.toFixed(2),
      data.totales.salarioNavidad.toFixed(2),
      data.totales.preaviso.toFixed(2),
      data.totales.cesantia.toFixed(2),
      data.totales.total.toFixed(2)
    ]);
    const csv = '\ufeff' + [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = scope === 'departamento'
      ? `prestaciones_${scopeName}_${new Date().toISOString().split('T')[0]}.csv`
      : `prestaciones_organizacion_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-500 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="space-y-5">

      {/* Resumen total */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${scope === 'departamento' ? 'bg-blue-50' : 'bg-green-50'}`}>
              {scope === 'departamento'
                ? <Building2 className="w-5 h-5 text-blue-600" />
                : <Users className="w-5 h-5 text-green-600" />
              }
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total a Pagar</p>
              <p className="text-3xl font-bold text-gray-900 mt-0.5">
                RD$ {fmt(data.totales.total)}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {data.totales.empleados} empleado{data.totales.empleados !== 1 ? 's' : ''}
                {scope === 'departamento' ? ` · ${scopeName}` : ' · Toda la organización'}
              </p>
            </div>
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Excel / CSV
          </button>
        </div>

        {/* Desglose de totales */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-gray-100">
          <SummaryCell
            icon={<Calendar className="w-4 h-4 text-blue-500" />}
            label="Vacaciones"
            value={data.totales.vacaciones}
            bg="bg-blue-50"
          />
          <SummaryCell
            icon={<CheckCircle className="w-4 h-4 text-green-500" />}
            label="Navidad"
            value={data.totales.salarioNavidad}
            bg="bg-green-50"
          />
          <SummaryCell
            icon={<AlertCircle className="w-4 h-4 text-orange-500" />}
            label="Preaviso"
            value={data.totales.preaviso}
            bg="bg-orange-50"
          />
          <SummaryCell
            icon={<Briefcase className="w-4 h-4 text-purple-500" />}
            label="Cesantía"
            value={data.totales.cesantia}
            bg="bg-purple-50"
          />
        </div>
      </div>

      {/* Por departamento — solo en scope organización */}
      {scope === 'organizacion' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {departments.map(dept => {
            const deptEmpleados = data.empleados.filter(p => p.empleado.departamento === dept.nombre);
            if (deptEmpleados.length === 0) return null;

            const deptTotal = deptEmpleados.reduce((s, p) => s + p.totalPrestaciones, 0);
            const pct = data.totales.total > 0
              ? Math.round((deptTotal / data.totales.total) * 100)
              : 0;

            return (
              <div key={dept.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-gray-100 p-1.5 rounded-lg">
                      <Building2 className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{dept.nombre}</p>
                      <p className="text-xs text-gray-400">{deptEmpleados.length} empleado{deptEmpleados.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 font-medium">{pct}%</span>
                </div>
                <p className="text-base font-bold text-gray-900">
                  RD$ {fmtShort(deptTotal)}
                </p>
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-400 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabla de detalle por empleado */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Detalle por Empleado
          </h3>
          <p className="text-xs text-gray-400">{data.empleados.length} registros</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
                  onClick={() => handleSort('nombre')}
                >
                  Empleado <SortIcon field="nombre" />
                </th>
                {scope === 'organizacion' && (
                  <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Departamento
                  </th>
                )}
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  Cargo
                </th>
                <th
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
                  onClick={() => handleSort('antiguedad')}
                >
                  Antigüedad <SortIcon field="antiguedad" />
                </th>
                <th
                  className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none hidden md:table-cell"
                  onClick={() => handleSort('salario')}
                >
                  Sal. Promedio <SortIcon field="salario" />
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  Vacac.
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Navidad
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Preaviso
                </th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  Cesantía
                </th>
                <th
                  className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-600 select-none"
                  onClick={() => handleSort('total')}
                >
                  Total <SortIcon field="total" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sortedEmpleados.map((prest) => (
                <tr key={prest.empleado.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">
                      {prest.empleado.nombre} {prest.empleado.apellido}
                    </p>
                  </td>
                  {scope === 'organizacion' && (
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        {prest.empleado.departamento || 'Sin dept'}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">
                    {prest.empleado.cargo}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {prest.anosServicio}a {prest.mesesServicio}m {prest.diasServicio ?? 0}d
                  </td>
                  <td className="px-4 py-3 text-xs text-right text-gray-600 hidden md:table-cell">
                    RD$ {fmt(prest.salarioPromedio)}
                  </td>
                  <td className="px-4 py-3 text-xs text-right text-gray-700">
                    RD$ {fmt(prest.vacaciones.monto)}
                  </td>
                  <td className="px-4 py-3 text-xs text-right text-gray-700 hidden sm:table-cell">
                    RD$ {fmt(prest.salarioNavidad.monto)}
                  </td>
                  <td className="px-4 py-3 text-xs text-right text-gray-700 hidden sm:table-cell">
                    RD$ {fmt(prest.preaviso.monto)}
                  </td>
                  <td className="px-4 py-3 text-xs text-right text-gray-700">
                    RD$ {fmt(prest.cesantia.monto)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">
                      RD$ {fmt(prest.totalPrestaciones)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50">
                <td
                  colSpan={scope === 'organizacion' ? 3 : 2}
                  className="px-4 py-3 text-xs font-bold text-gray-700"
                >
                  TOTALES · {data.totales.empleados} empleados
                </td>
                <td className="hidden md:table-cell" />
                <td className="px-4 py-3 text-xs text-right font-semibold text-gray-700">
                  RD$ {fmt(data.totales.vacaciones)}
                </td>
                <td className="px-4 py-3 text-xs text-right font-semibold text-gray-700 hidden sm:table-cell">
                  RD$ {fmt(data.totales.salarioNavidad)}
                </td>
                <td className="px-4 py-3 text-xs text-right font-semibold text-gray-700 hidden sm:table-cell">
                  RD$ {fmt(data.totales.preaviso)}
                </td>
                <td className="px-4 py-3 text-xs text-right font-semibold text-gray-700">
                  RD$ {fmt(data.totales.cesantia)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-bold text-blue-700">
                    RD$ {fmt(data.totales.total)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ icon, label, value, bg }) {
  return (
    <div className={`${bg} rounded-lg p-3 flex items-center gap-3`}>
      <div className="flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] text-gray-500 font-medium">{label}</p>
        <p className="text-xs font-bold text-gray-900 truncate">RD$ {fmt(value)}</p>
      </div>
    </div>
  );
}
